import React, { useState, useEffect } from 'react';
import { featuresAPI } from '../../services/api';
import Button from '../common/Button';
import Loading from '../common/Loading';
import { handleApiError } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';

const FeatureFormModal = ({ feature, onClose, onSuccess }) => {
  const { isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    description: '',
    description_ar: '',
    icon: '',
    display_order: 0,
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (feature) {
      setFormData({
        name: feature.name || '',
        name_ar: feature.name_ar || '',
        description: feature.description || '',
        description_ar: feature.description_ar || '',
        icon: feature.icon || '',
        display_order: feature.display_order || 0,
        is_active: feature.is_active !== false,
      });
    }
  }, [feature]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : type === 'number' ? (value === '' ? '' : parseInt(value)) : value,
    });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        display_order: parseInt(formData.display_order) || 0,
      };

      if (feature) {
        await featuresAPI.update(feature.id, submitData);
      } else {
        if (!submitData.name) {
          throw new Error('Name is required');
        }
        await featuresAPI.create(submitData);
      }
      onSuccess();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {feature ? 'Edit Feature' : 'Add New Feature'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name (English) */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name (English) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Unlimited Posts"
                  className="input w-full"
                />
              </div>

              {/* Name (Arabic) */}
              <div>
                <label htmlFor="name_ar" className="block text-sm font-medium text-gray-700 mb-2">
                  Name (Arabic)
                </label>
                <input
                  type="text"
                  id="name_ar"
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleChange}
                  placeholder="مثل: بوستات غير محدودة"
                  className="input w-full"
                  dir="rtl"
                />
              </div>
            </div>

            {/* Description */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description (English)
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="3"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Feature description..."
                  className="textarea w-full"
                />
              </div>

              <div>
                <label htmlFor="description_ar" className="block text-sm font-medium text-gray-700 mb-2">
                  Description (Arabic)
                </label>
                <textarea
                  id="description_ar"
                  name="description_ar"
                  rows="3"
                  value={formData.description_ar}
                  onChange={handleChange}
                  placeholder="وصف الميزة..."
                  className="textarea w-full"
                  dir="rtl"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Icon */}
              <div>
                <label htmlFor="icon" className="block text-sm font-medium text-gray-700 mb-2">
                  Icon (Emoji or icon name)
                </label>
                <input
                  type="text"
                  id="icon"
                  name="icon"
                  value={formData.icon}
                  onChange={handleChange}
                  placeholder="e.g., ✓, 🌟, 📝"
                  className="input w-full"
                  maxLength={50}
                />
                <p className="text-xs text-gray-500 mt-1">Enter an emoji or icon identifier</p>
              </div>

              {/* Display Order */}
              <div>
                <label htmlFor="display_order" className="block text-sm font-medium text-gray-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  id="display_order"
                  name="display_order"
                  min="0"
                  value={formData.display_order}
                  onChange={handleChange}
                  className="input w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
              </div>
            </div>

            {/* Active Status */}
            {feature && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className={`ml-2 block text-sm text-gray-700 ${isRTL ? 'mr-2 ml-0' : ''}`}>
                  Feature is active (visible in plans)
                </label>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Saving...' : feature ? 'Update Feature' : 'Create Feature'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeatureFormModal;
